/**
 * Create Actions - Snippet creation with UI integration
 *
 * This action handles snippet creation with full UI integration:
 * - Creates the snippet via the API
 * - Shows success/error flags
 * - Adds the snippet to the editor's quick insert menu
 * - Refreshes the snippets list
 *
 * Separated from fetchActions because it has UI dependencies (flags, intl, editor API)
 * that the pure data operations don't need.
 */
import type { RefObject } from 'react';
import type { StoreActionApi } from 'react-sweet-state';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { IntlShape } from 'react-intl-next';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import * as BlockServiceClient from '../../services/blockServiceClient.ts';
import type { CreateSnippetBody } from '../../services/blockServiceClient.ts';
import { SnippetBodyFormat, SnippetProduct } from '../../snippetTypes.ts';
import type { Snippet } from '../../snippetTypes.ts';
import type { SnippetsState } from '../snippetsStore.types.ts';
import { messages } from '../../modals/CreateSnippetModal.i18n.ts';
import { addSnippetQuickInsertItem } from '../../quick-insert/addSnippetQuickInsertItem.tsx';

/**
 * Options for createSnippet that enable UI feedback and editor integration
 */
export type CreateSnippetOptions = {
	/** Flags API for showing success/error notifications */
	flags: WithFlagsProps['flags'];
	/** Intl for message formatting */
	intl: IntlShape;
	/** Editor API ref for adding snippet to quick insert menu after creation */
	editorAPIRef: RefObject<PublicPluginAPI<[QuickInsertPlugin]> | null | undefined>;
};

/**
 * Shows a success flag after snippet creation
 */
const displaySuccessFlag = (
	flags: WithFlagsProps['flags'],
	intl: IntlShape,
	snippetName: string,
) => {
	flags.showSuccessFlag({
		title: intl.formatMessage(messages.createTemplateModalSuccessFlagTitle, {
			snippetName,
		}),
		description: intl.formatMessage(messages.createTemplateModalSuccessFlagDescription),
		isAutoDismiss: true,
	});
};

/**
 * Shows an error flag when snippet creation fails
 */
const displayErrorFlag = (flags: WithFlagsProps['flags'], intl: IntlShape) => {
	flags.showErrorFlag({
		title: intl.formatMessage(messages.createTemplateModalErrorFlagTitle),
		description: intl.formatMessage(messages.createTemplateModalErrorFlagDescription),
		isAutoDismiss: true,
	});
};

/**
 * Reports an error to the monitoring client
 */
const reportError = (error: Error) => {
	getMonitoringClient().submitError(error, {
		attribution: Attribution.INTEGRATIONS,
	});
};

/**
 * Actions for creating snippets with UI integration.
 */
export const createActions = {
	/**
	 * Creates a new snippet and refreshes the snippets list.
	 *
	 * After successful creation:
	 * - Clears the service cache and fetches the updated list
	 * - Shows a success flag (if options provided)
	 * - Adds the snippet to the quick insert menu
	 *
	 * On error:
	 * - Reports to monitoring
	 * - Shows an error flag (if options provided)
	 * - Keeps existing snippets visible (doesn't reset isLoaded)
	 *
	 * @param name - The snippet name (required)
	 * @param body - The ADF content body
	 * @param description - Optional description text
	 * @param options - UI options including flags, intl, and editor API ref
	 * @returns Promise resolving to the newly created snippet, or null on failure
	 */
	createSnippet:
		(name: string, body: ADFEntity, description?: string, options?: CreateSnippetOptions) =>
		async ({ setState }: StoreActionApi<SnippetsState>): Promise<Snippet | null> => {
			const params: CreateSnippetBody = {
				name,
				description,
				body: JSON.stringify(body),
				bodyFormat: SnippetBodyFormat.ADF,
				product: SnippetProduct.CONFLUENCE,
			};

			setState({ isLoading: true, error: null });

			try {
				const newSnippet = await BlockServiceClient.createSnippet(params);

				// Clear the service-level cache and refresh to get the complete updated list
				BlockServiceClient.clearSnippetsCache();
				const updatedSnippets = await BlockServiceClient.fetchSnippets();

				setState({
					snippets: updatedSnippets,
					isLoading: false,
					error: null,
					isLoaded: true,
				});

				// Handle UI feedback and editor integration if options provided
				if (options) {
					const { flags, intl, editorAPIRef } = options;
					displaySuccessFlag(flags, intl, newSnippet.name);
					addSnippetQuickInsertItem({ snippet: newSnippet, editorAPIRef });
				}

				return newSnippet;
			} catch (thrownError) {
				const err = thrownError instanceof Error ? thrownError : new Error(String(thrownError));

				setState({
					isLoading: false,
					error: err,
					// Don't reset isLoaded on create error - keep existing snippets visible
				});

				reportError(err);

				// Handle UI feedback if options provided, otherwise throw for backward compatibility
				if (options) {
					displayErrorFlag(options.flags, options.intl);
					return null;
				}

				throw err;
			}
		},
};
